<template>
  <v-dialog max-width="900px" v-model="open" scrollable>
    <template v-slot:activator="{ props: activatorProps }">
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-card
          v-bind="activatorProps"
          @click="recordOpen()"
          class="listing-card"
          hover
          elevation="2"
        >
          <!-- Image Section -->
          <div style="position: relative; height: 220px; overflow: hidden; background-color: #f5f5f5;">
            <v-img
              :src="getImageUrl()"
              height="220"
              contain
            >
              <template v-slot:error>
                <v-img height="220" contain :src="require('@/assets/no-img.png')" />
              </template>
              <template v-slot:placeholder>
                <v-skeleton-loader height="220" type="image" />
              </template>
            </v-img>

            <!-- Badge Overlay with Dark Background -->
            <div style="position: absolute; top: 8px; left: 8px; z-index: 2; display: flex; flex-direction: column; gap: 4px;">
              <v-chip
                v-if="item.cemeteryid > -1"
                size="small"
                color="primary"
                prepend-icon="mdi-office-building-marker"
                style="background-color: rgba(0, 0, 0, 0.75) !important; color: white !important;"
              >
                Official
              </v-chip>
              <v-chip
                v-if="item.premium == 1"
                size="small"
                color="purple"
                prepend-icon="mdi-star"
                style="background-color: rgba(156, 39, 176, 0.9) !important; color: white !important;"
              >
                Premium
              </v-chip>
            </div>
          </div>

          <!-- Content Section -->
          <v-card-text class="pb-2">
            <div class="d-flex justify-space-between align-center mb-2">
              <h3 class="text-h6 font-weight-bold text-truncate">
                {{ item.spacenumber }}
              </h3>
              <div class="text-h6 font-weight-bold primary--text">
                <span v-if="item.cost && item.cost > 0">
                  ${{ item.cost.toLocaleString(undefined, { minimumFractionDigits: 0 }) }}
                </span>
              </div>
            </div>

            <p class="text-subtitle-1 text-truncate mb-1">
              {{ item.cemeteryname }}
            </p>

            <div class="d-flex align-center text-body-2 text-medium-emphasis">
              <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
              {{ item.city }}, {{ item.state }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </template>

    <!-- Dialog Content -->
    <template v-slot:default="{ isActive }">
      <v-card>
        <!-- Header -->
        <v-toolbar color="primary" dark>
          <v-toolbar-title class="text-h5">
            Space {{ item.spacenumber }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="isActive.value = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0">
          <!-- Image -->
          <v-img
            :src="getImageUrl()"
            max-height="500"
            cover
          >
            <template v-slot:error>
              <v-img max-height="500" cover :src="require('@/assets/no-img.png')" />
            </template>
            <template v-slot:placeholder>
              <v-skeleton-loader height="500" type="image" />
            </template>
          </v-img>

          <!-- Details Section -->
          <v-container class="py-6">
            <v-row>
              <!-- Left Column - Main Info -->
              <v-col cols="12" md="8">
                <div class="mb-6">
                  <h2 class="text-h4 font-weight-bold mb-2">
                    <span v-if="item.cost && item.cost > 0">
                      ${{ item.cost.toLocaleString(undefined, { minimumFractionDigits: 2 }) }}
                    </span>
                    <span v-else class="text-h5">Contact for Price</span>
                  </h2>
                  <div class="d-flex align-center gap-2 mb-2">
                    <v-chip
                      v-if="item.cemeteryid > -1"
                      color="primary"
                      variant="flat"
                      size="small"
                    >
                      <v-icon start size="small">mdi-office-building-marker</v-icon>
                      Official Cemetery Listing
                    </v-chip>
                    <v-chip
                      v-if="item.premium == 1"
                      color="purple"
                      variant="flat"
                      size="small"
                    >
                      <v-icon start size="small">mdi-star</v-icon>
                      Premium
                    </v-chip>
                  </div>
                </div>

                <v-divider class="my-4" />

                <!-- Description -->
                <div class="mb-6">
                  <h3 class="text-h6 font-weight-bold mb-3">Description</h3>
                  <p class="text-body-1" style="white-space: pre-line;">
                    {{ item.description || 'No description provided.' }}
                  </p>
                </div>
              </v-col>

              <!-- Right Column - Listing Details -->
              <v-col cols="12" md="4">
                <v-card variant="outlined" class="pa-4">
                  <h3 class="text-h6 font-weight-bold mb-4">Listing Details</h3>
                  
                  <div class="mb-4">
                    <div class="text-caption text-medium-emphasis mb-1">Cemetery</div>
                    <div class="text-body-1 font-weight-medium">{{ item.cemeteryname }}</div>
                  </div>

                  <div class="mb-4">
                    <div class="text-caption text-medium-emphasis mb-1">Location</div>
                    <div class="text-body-1">
                      <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                      {{ item.city }}, {{ item.state }}
                    </div>
                    <div class="text-body-2 text-medium-emphasis" v-if="item.county">
                      {{ item.county }} County
                    </div>
                  </div>

                  <div class="mb-4">
                    <div class="text-caption text-medium-emphasis mb-1">Seller</div>
                    <div class="text-body-1 font-weight-medium">{{ item.sellername }}</div>
                  </div>

                  <div class="mb-4" v-if="item.spacetype">
                    <div class="text-caption text-medium-emphasis mb-1">Type of Space</div>
                    <div class="text-body-1">{{ item.spacetype }}</div>
                  </div>

                  <div class="mb-4" v-if="item.intermenttypes">
                    <div class="text-caption text-medium-emphasis mb-1">Interment Types Allowed</div>
                    <div class="text-body-1">{{ item.intermenttypes }}</div>
                  </div>

                  <div class="mb-4" v-if="item.restrictions">
                    <div class="text-caption text-medium-emphasis mb-1">Known Restrictions</div>
                    <div class="text-body-2" style="white-space: pre-line;">
                      {{ item.restrictions }}
                    </div>
                  </div>

                  <v-divider class="my-4" />

                  <!-- Contact Button -->
                  <ContactDialog :item="item" />
                </v-card>
              </v-col>
            </v-row>

            <!-- Map Section - Full Width -->
            <v-row>
              <v-col cols="12">
                <v-card variant="outlined" class="pa-4">
                  <h3 class="text-h6 font-weight-bold mb-3">Location</h3>
                  <div :style="{ width: '100%', height: $vuetify.display.mdAndUp ? '400px' : '250px', borderRadius: '8px', overflow: 'hidden' }">
                    <iframe
                      width="100%"
                      height="100%"
                      style="border:0;"
                      loading="lazy"
                      :src="getMapUrl()"
                      allowfullscreen
                    ></iframe>
                  </div>
                  <v-btn
                    :href="getDirectionsUrl()"
                    target="_blank"
                    variant="text"
                    color="primary"
                    class="mt-2"
                  >
                    <v-icon start>mdi-directions</v-icon>
                    Get Directions
                  </v-btn>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <!-- Footer Actions -->
        <v-divider />
        <v-card-actions class="pa-4">
          <v-btn
            variant="text"
            color="error"
            prepend-icon="mdi-flag"
            @click="reportListing"
          >
            Report Listing
          </v-btn>
          <v-spacer />
          <v-btn
            variant="outlined"
            @click="isActive.value = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>

  <!-- Report Confirmation Dialog -->
  <v-dialog v-model="reportDialog" max-width="500">
    <v-card>
      <v-card-title class="bg-error text-white">
        <v-icon class="mr-2">mdi-flag</v-icon>
        Report This Listing
      </v-card-title>
      <v-card-text class="pt-4">
        <p class="mb-4">Are you sure you want to report this listing for review?</p>
        <v-textarea
          v-model="reportReason"
          label="Reason for reporting (optional)"
          variant="outlined"
          rows="3"
          placeholder="Please describe the issue..."
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="reportDialog = false">
          Cancel
        </v-btn>
        <v-btn color="error" variant="flat" @click="submitReport">
          Submit Report
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Success Snackbar -->
  <v-snackbar
    v-model="snackbar"
    :color="snackbarColor"
    :timeout="3000"
    location="top"
  >
    {{ snackbarMessage }}
    <template v-slot:actions>
      <v-btn variant="text" @click="snackbar = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script>
import ContactDialog from './ContactDialog.vue';

export default {
  name: 'MarketItem',

  components: {
    ContactDialog
  },

  props:{
    item : Object
  },

  data: () => ({
    open : false,
    reportDialog: false,
    reportReason: '',
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
  }),

  methods:{
    getImageUrl() {
      return `https://ewr1.vultrobjects.com/crvd/cc-marketplace/${this.item.id}/1.jpg`;
    },
    recordOpen(){
      fetch("https://app.cemeterycommunity.com:11000/api/addmarketplaceitemlog", { method: "POST", headers : { data : JSON.stringify( { type : 'Listing Viewed', itemid : this.item.id } ) } });
    },
    reportListing() {
      this.reportDialog = true;
    },
    getMapUrl() {
      const query = encodeURIComponent(`${this.item.cemeteryname}, ${this.item.city}, ${this.item.state}`);
      return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${query}&zoom=15`;
    },
    getDirectionsUrl() {
      const query = encodeURIComponent(`${this.item.cemeteryname}, ${this.item.city}, ${this.item.state}`);
      return `https://www.google.com/maps/dir/?api=1&destination=${query}`;
    },
    submitReport() {
      // Flag the listing in the database
      fetch("https://app.cemeterycommunity.com:11000/api/flagmarketitem", { 
        method: "POST", 
        headers: { 
          data: JSON.stringify({ 
            id: this.item.id,
            reason: this.reportReason || 'No reason provided'
          }) 
        } 
      })
      .then(res => res.json())
      .then(res => {
        if(res.results === "Success") {
          // Also log the report
          fetch("https://app.cemeterycommunity.com:11000/api/addmarketplaceitemlog", { 
            method: "POST", 
            headers: { 
              data: JSON.stringify({ 
                type: 'Listing Reported', 
                itemid: this.item.id,
                reason: this.reportReason || 'No reason provided'
              }) 
            } 
          });
          
          this.reportDialog = false;
          this.open = false;
          this.reportReason = '';
          this.snackbarMessage = 'Thank you for your report. We will review this listing.';
          this.snackbarColor = 'success';
          this.snackbar = true;
        } else {
          this.snackbarMessage = 'Failed to submit report. Please try again.';
          this.snackbarColor = 'error';
          this.snackbar = true;
        }
      })
      .catch(() => {
        this.snackbarMessage = 'Failed to submit report. Please try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      });
    }
  }
}
</script>